import { useContext, useState } from 'react';
import { EyeIcon } from '@phosphor-icons/react';
import { T, Var, useGT } from 'gt-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { AuthContext } from '@/context/auth.context';
import { useViewMode } from '@/context/view-mode.context';

const ADMIN_TOOLS_SWITCH_ID = 'imitation-admin-tools';

/**
 * Full-width warning strip shown while a Gently admin is imitating a user.
 *
 * Deliberately in-flow rather than fixed: this is the one banner that must
 * never be missed or overlapped, so it takes real layout space above the
 * header instead of floating over it like `DeploymentEnvBanner`.
 *
 * `yellow-950` on `yellow-400` clears AA comfortably (~11:1), and the exit
 * button inverts to a near-white pill so it reads as the actionable element on
 * a field of yellow.
 *
 * Everything in `currentUser` describes the IMITATED user — the admin driving
 * the session is `currentUser.imitation.realUser`.
 *
 * It also carries the "Admin tools" switch, because this banner is the only
 * chrome that exists solely while imitating. `/auth/me` reports the target's
 * `isAdmin` (false), so every admin-gated surface vanishes from the real admin
 * mid-session; the switch opts back into the read-only diagnostic ones. It
 * defaults off so the imitated view stays faithful, and it changes frontend
 * visibility only — the server still authorizes against the imitated user and
 * still vetoes writes.
 */
export function ImitationBanner() {
  const gt = useGT();
  const { currentUser, stopImitation } = useContext(AuthContext);
  const { adminToolsWhileImitating, setAdminToolsWhileImitating } =
    useViewMode();
  const [isStopping, setIsStopping] = useState(false);

  const imitation = currentUser?.imitation;
  if (!imitation) {
    return null;
  }

  const handleExit = async () => {
    setIsStopping(true);
    try {
      // Resolves without reloading only when stopping failed, in which case
      // the button has to come back.
      await stopImitation();
    } catch {
      // stopImitation never rejects (it logs internally); the clause exists so
      // the React Compiler doesn't bail on a catch-less try (react-hooks todo).
    } finally {
      setIsStopping(false);
    }
  };

  return (
    <div
      role="status"
      className="flex shrink-0 flex-wrap items-center justify-center gap-3 bg-yellow-400 px-4 py-1.5 text-sm font-medium text-yellow-950"
    >
      <EyeIcon className="size-4 shrink-0" weight="fill" />
      <span className="truncate">
        <T context="banner shown to a Gently admin while they are viewing the app as another user">
          Viewing as <Var>{currentUser.name}</Var> — view-only mode
        </T>
      </span>
      <div className="flex items-center gap-1.5">
        {/* The Switch is the TooltipTrigger's direct child so `aria-describedby`
            lands on the focusable control itself, not on a layout wrapper. */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Switch
              id={ADMIN_TOOLS_SWITCH_ID}
              checked={adminToolsWhileImitating}
              onCheckedChange={setAdminToolsWhileImitating}
              className="data-[checked]:bg-yellow-950 data-[unchecked]:bg-yellow-950/25 dark:data-[unchecked]:bg-yellow-950/25"
            />
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            {gt(
              'Show Gently admin-only diagnostic pages, such as Feature Flags, while imitating. Off keeps this session identical to what the user sees.',
              {
                context:
                  'tooltip for the imitation banner switch that restores admin-only diagnostic pages',
              },
            )}
          </TooltipContent>
        </Tooltip>
        <label
          htmlFor={ADMIN_TOOLS_SWITCH_ID}
          className="cursor-pointer whitespace-nowrap"
        >
          <T context="switch label in the imitation banner; turns Gently admin-only diagnostic pages back on for the admin doing the imitating">
            Admin tools
          </T>
        </label>
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={isStopping}
        onClick={handleExit}
        className="border-yellow-950/30 bg-yellow-50 text-yellow-950 hover:bg-yellow-100 hover:text-yellow-950 dark:bg-yellow-50 dark:border-yellow-950/30 dark:hover:bg-yellow-100"
      >
        {isStopping ? (
          <T context="button label while imitation mode is being ended">
            Exiting…
          </T>
        ) : (
          <T context="button that ends imitation mode and returns the admin to their own account">
            Exit imitation mode
          </T>
        )}
      </Button>
    </div>
  );
}
